import {
  RecoClickEvent,
  RecoEventContext,
  RecoProductBasedRecommendationsItemData,
  RecoViewEvent,
  AbTestEvent,
} from "@cube/tracking-library-api";
import RecoTracking from "../RecoTracking";
import {
  RecommendationsTrackingDataDto,
  FarbartikelTrackingDataDto,
  GeschenkkarteTrackingDataDto,
} from "../RecommendationsTrackingDataDto";
import { PromotionData } from "@cube/tracking-library-api/dist/@types/events/fragments";

const layer = "layer" as const;
const page = "page" as const;
const addToCartLayer = "add_to_cart_page.1" as const;
const wvbLayer = "item_page.wvb_layer" as const;

const getPlacement: (p: string) => "layer" | "page" = (p) => {
  switch (p) {
    case addToCartLayer:
      return layer;
    case wvbLayer:
      return layer;
    default:
      return page;
  }
};

const getEventContext: (p: string) => RecoEventContext = (p) =>
  p === addToCartLayer ? { event_context: "a2c" } : {};

const getPromotionData: (
  data: RecommendationsTrackingDataDto,
) => PromotionData = (data) => ({
  placement: getPlacement(data.placement),
  name: data.placement,
  slot: data.ssiName,
  rule: data.strategie,
  treatment: data.treatment,
  type: "slider",
});

export default class CubeRecoTracking extends RecoTracking {
  trackRecoClick(data: RecommendationsTrackingDataDto, index: number): void {
    const item = data.items.filter((_, i) => i === index);

    const recoEvent: RecoClickEvent = {
      event_name: "reco_click",
      responsible_team_tracking: "RECO",
      responsible_team_feature: "RECO",
      currency: {
        code: data.currency,
      },
      event_data: {
        is_interaction_event: true,
        ...getEventContext(data.placement),
      },
      items: item
        .map((art) => {
          switch (art.typeTag) {
            case "FarbartikelTrackingDataDto":
              return CubeRecoTracking.farbartikelTrackingDataToRecoItemData(
                art,
                index,
              );
            case "GeschenkkarteTrackingDataDto":
              return CubeRecoTracking.geschenkkarteTrackingDataDtoToRecoItemData(
                art,
                index,
              );
          }
        })
        .filter(
          (data): data is RecoProductBasedRecommendationsItemData =>
            data !== null,
        ),
      promotion_data: getPromotionData(data),
    };
    window.CubeDataLayer?.push(recoEvent);
  }

  trackRecoImpression(
    data: RecommendationsTrackingDataDto,
    visibleIndices: Array<string>,
  ): void {
    const cubeRecoEvent: RecoViewEvent = {
      event_name: "reco_view",
      responsible_team_tracking: "RECO",
      responsible_team_feature: "RECO",
      currency: {
        code: data.currency,
      },
      event_data: {
        is_interaction_event: false,
        ...getEventContext(data.placement),
      },
      items: data.items
        .map((art, index) => {
          switch (art.typeTag) {
            case "FarbartikelTrackingDataDto":
              return CubeRecoTracking.farbartikelTrackingDataToRecoItemData(
                art,
                index,
              );
            case "GeschenkkarteTrackingDataDto":
              return CubeRecoTracking.geschenkkarteTrackingDataDtoToRecoItemData(
                art,
                index,
              );
          }
        })
        .filter((_, index) => visibleIndices.includes(index.toString(10))),
      promotion_data: getPromotionData(data),
    };
    window.CubeDataLayer?.push(cubeRecoEvent);
  }

  private static farbartikelTrackingDataToRecoItemData(
    farbartikelTrackingData: FarbartikelTrackingDataDto,
    index: number,
  ): RecoProductBasedRecommendationsItemData {
    return {
      item_type: "Product",
      is_sponsored: false,
      target_url: farbartikelTrackingData.produktLink,
      position: index + 1,
      syan: farbartikelTrackingData.syan,
      farb_id: farbartikelTrackingData.farbId,
    };
  }

  private static geschenkkarteTrackingDataDtoToRecoItemData(
    geschenkkarteTrackingData: GeschenkkarteTrackingDataDto,
    index: number,
  ): RecoProductBasedRecommendationsItemData {
    return {
      item_type: "Giftcard",
      is_sponsored: false,
      target_url: geschenkkarteTrackingData.produktLink,
      position: index + 1,
    };
  }

  trackABTest(testingVariant: string | null | undefined): void {
    if (testingVariant) {
      const event: AbTestEvent = {
        event_name: "ab_test",
        responsible_team_feature: "RECO",
        responsible_team_tracking: "RECO",
        event_data: {
          event_category: "site_variation",
          event_type: "test",
          is_interaction_event: false,
          event_context: "{{add A/B test name here}}",
          event_content: [testingVariant],
        },
      };
      window.CubeDataLayer?.push(event);
    }
  }
}
